import React, { FC, useEffect } from 'react';
import useTitleBarSelect from '../../../../hooks/useTitleBarSelect';
import CisTable, { ExposureType } from './table/cisTable.component';
import CisMaps from './maps/CisMaps';
import {
  addAdditionalTitleBarComponents,
  removeTitleBarComponent,
} from '../../../../redux/pages/actions';
import {
  APPEND_NEW_SELECT_TO_TITLEBAR,
  REMOVE_SELECT_FROM_TITLEBAR,
} from '../../../../types/redux/pages/pageTypes';
import { useDispatch } from 'react-redux';
import { Select } from '../../../../types/redux/pages/PagesStore';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useAssetClassReportPageModel } from '../../../../api/rs-hg/asset-class/hooks/queries';
import { CisServerData, isCisServerData } from './cis.data';

const Cis: FC<FundInfoComponentProps> = ({ fundId, positionDate }) => {
  const dispatch = useDispatch();

  // Add the select components to the title bar
  useEffect(() => {
    const selectsToAdd: Select[] = [
      {
        titleBarTitle: 'Type: ',
        titleBarKey: 'exposureType',
        values: [
          {
            label: 'Net Exposure, Base Currency',
            value: 'netExposureBaseCurrency',
          },
          {
            label: 'Net Exposure, % NAV',
            value: 'netExposurePcNav',
          },
        ],
        displayOnOverviewPage: false,
        aValueIsAlwaysSelected: true,
        currentValue: 'netExposureBaseCurrency',
      },
      {
        titleBarTitle: 'Visualisation: ',
        titleBarKey: 'selectVisualisation',
        values: [
          {
            label: 'Position Table',
            value: 'positionTable',
          },
          {
            label: 'Country Exposure',
            value: 'countryExposure',
          },
        ],
        displayOnOverviewPage: false,
        aValueIsAlwaysSelected: true,
        currentValue: 'positionTable',
      },
    ];

    dispatch(
      addAdditionalTitleBarComponents(
        selectsToAdd,
        APPEND_NEW_SELECT_TO_TITLEBAR,
      ),
    );

    return () => {
      dispatch(
        removeTitleBarComponent('exposureType', REMOVE_SELECT_FROM_TITLEBAR),
      );
      dispatch(
        removeTitleBarComponent(
          'selectVisualisation',
          REMOVE_SELECT_FROM_TITLEBAR,
        ),
      );
    };
  }, []);

  const exposureType = useTitleBarSelect('exposureType');
  const selectedVisualisation = useTitleBarSelect('selectVisualisation');

  const { data, isLoading, isFetching, isError, error } = useAssetClassReportPageModel<CisServerData>({
    fundId,
    reportKey: 'cis_specific_data',
    positionDate,
    isReportData: isCisServerData,
  });

  return (
    <QueryBoundary<CisServerData>
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      error={error}
      data={data?.data}
      errorMessage="No In-Scope or Available Data"
      noDataMessage="No In-Scope or Available Data"
    >
      {(report) => {
        const selectedExposure =
          (exposureType as ExposureType) || 'netExposureBaseCurrency';

        return selectedVisualisation === 'positionTable' ? (
          <CisTable
            cisData={report}
            isFetching={isFetching}
            selectedExposureType={selectedExposure}
          />
        ) : (
          <CisMaps
            cisData={report}
            isFetching={isFetching}
            selectedExposureType={selectedExposure}
          />
        );
      }}
    </QueryBoundary>
  );
};

export default Cis;
